/**
 * Update asset request using the same endpoint, but as an 'after update' variant for clarity
 */

import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  Observable,
  throwError,
  of,
  firstValueFrom,
} from 'rxjs';
import { map, tap } from 'rxjs/operators';
import memoizeObservable from '@app/shared/utils/memoizedObservable';
import { environment } from '@environments/environment';
import { ToastrService } from 'ngx-toastr';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  VendorStatsCard,
  VendorSummary,
} from '@app/shared/interfaces/vendor-stats';
import {
  VendorStatsCardEnum,
  DamageAnalyticsCardEnum,
} from '@app/shared/constants/global.enum';
import {
  InventoryTableData,
  InventoryHistoryData,
  InventoryOrderTableData,
} from '@app/shared/interfaces/inventory-data';
import { OnboardingRequest } from './asset-management-table/onboarding-requests-table.service';
import { OffboardingRequest } from './asset-management-table/offboarding-requests-table.service';
import { formatSingleDate } from '@utils/utils';
import { yearDateFormat } from '@constants/dateFormat';
import { OffboardingDetails } from '@app/shared/interfaces/offboarding.model';
import { DamageRecord } from '@app/shared/interfaces/asset-management.model';
import { ASSET_MANAGEMENT_API } from './asset-management.api';

export interface AssetAllocationResponse {
  status: string;
  data: Record<string, Record<string, number>>;
  message: string;
}

export interface ProcessedAssetData {
  location: string;
  macbook: number;
  windows: number;
}

export interface EmailNotificationResponse {
  location: string;
  to: string[];
  cc: string[];
}

export type CardType =
  | 'header'
  | 'My-Request'
  | 'onboarding'
  | 'offboarding'
  | 'asset-allocated'
  | 'inventory'
  | 'inventory-order'
  | 'vendor-stats'
  | 'damage-analytics';

@Injectable({
  providedIn: 'root',
})
export class AssetManagementService {
  private static accessoriesCache: { label: string; value: string }[] = [];
  private static isLoadingAccessories = false;
  private static accessoriesCachePromise: Promise<any[]> | null = null;

  // Remove the old static caching infrastructure since we're using memoizeObservable
  // private static inventoryCache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();
  // private static readonly DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

  getAccessoriesCache(): { label: string; value: string }[] {
    return [...AssetManagementService.accessoriesCache];
  }

  isAccessoriesLoaded(): boolean {
    return AssetManagementService.accessoriesCache.length > 0;
  }

  async preloadAccessoriesData(): Promise<void> {
    if (AssetManagementService.accessoriesCache.length > 0) {
      return;
    }

    if (
      AssetManagementService.isLoadingAccessories &&
      AssetManagementService.accessoriesCachePromise
    ) {
      await AssetManagementService.accessoriesCachePromise;
      return;
    }

    AssetManagementService.isLoadingAccessories = true;
    AssetManagementService.accessoriesCachePromise =
      this.fetchAccessoriesData();

    try {
      await AssetManagementService.accessoriesCachePromise;
    } finally {
      AssetManagementService.isLoadingAccessories = false;
    }
  }

  private async fetchAccessoriesData(): Promise<any[]> {
    try {
      const response: any = await firstValueFrom(
        this.https.get(ASSET_MANAGEMENT_API.ACCESSORIES_API_URL_AFTER_UPDATE),
      );

      if (Array.isArray(response?.data) && response.data.length > 0) {
        AssetManagementService.accessoriesCache = response.data.map(
          (item: any) => ({
            label: item?.accessoriesType ?? '',
            value: item?.accessoriesType ?? '',
          }),
        );
      }
      return AssetManagementService.accessoriesCache;
    } catch (error) {
      return [];
    }
  }

  clearAccessoriesCache(): void {
    AssetManagementService.accessoriesCache = [];
    AssetManagementService.accessoriesCachePromise = null;
    AssetManagementService.isLoadingAccessories = false;
  }

  getAllAssetCardDetails(
    startDate?: string,
    endDate?: string,
  ): Observable<any> {
    let url = ASSET_MANAGEMENT_API.ALL_ASSET_CARD_DETAILS;

    // Add query parameters if provided
    const params = [];
    if (startDate && startDate.trim() !== '') {
      params.push(`startDate=${encodeURIComponent(startDate)}`);
    }
    if (endDate && endDate.trim() !== '') {
      params.push(`endDate=${encodeURIComponent(endDate)}`);
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    // Add skip-loader header to prevent global loader and use card-specific loader instead
    const headers = new HttpHeaders({ 'skip-loader': 'true' });

    return this.https.get<any>(url, { headers }).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to fetch asset card details.');
        return throwError(() => error);
      }),
    );
  }

  private readonly baseUrl = environment.apiUrl;
  private readonly assetManagementBaseUrl = `${this.baseUrl}/asset-management`;

  updateReturnStatusOffboard(payload: {
    employeeId: string;
    newReturnStatus: string;
    newReturnDate?: string;
  }): Observable<any> {
    const url = ASSET_MANAGEMENT_API.UPDATE_RETURN_STATUS;
    return this.https
      .put<any>(url, payload, { responseType: 'text' as 'json' })
      .pipe(
        map(() => ({ success: true })),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }
  updateReturnStatus(payload:any): Observable<any> {
    const url = ASSET_MANAGEMENT_API.UPDATE_RETURN_STATUS;
    return this.https
      .put<any>(url, payload, { responseType: 'text' as 'json' })
      .pipe(
        map(() => ({ success: true })),
        catchError((error) => {
          this.toasterService.error(error?.error );
          return throwError(() => error);
        }),
      );
  }

  private readonly selectedCardSource = new BehaviorSubject<CardType | null>(
    null,
  );
  public selectedCard$: Observable<CardType | null> =
    this.selectedCardSource.asObservable();

  constructor(
    private toasterService: ToastrService,
    private https: HttpClient,
  ) {}

  //  * Get user's location from session storage
  getUserLocationFromSession(): string {
    try {
      const userProfileString = sessionStorage.getItem('userProfile');
      if (!userProfileString) return 'Hyderabad';
      const userProfile = JSON.parse(userProfileString);
      return userProfile?.city || 'Hyderabad';
    } catch (error) {
      console.error('Error reading userProfile from session storage:', error);
      return 'Hyderabad';
    }
  }

  createOffboardingRequest(request: {
    employeeId: string;
    dateOfReturn: string;
    dateOfOffboarding?: string;
    status?: string;
    comments?: string;
    commentsLabel?: string;
  }): Observable<any> {
    const url = ASSET_MANAGEMENT_API.CREATE_OFFBOARDING_REQUEST;
    return this.https
      .put(url, request, { responseType: 'text' as 'json' })
      .pipe(
        map(() => ({ success: true })),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  getOffboardingData(
    startDate: string,
    endDate: string,
  ): Observable<{ data: OffboardingRequest[] }> {
    startDate = formatSingleDate(startDate, yearDateFormat.date);
    endDate = formatSingleDate(endDate, yearDateFormat.date);
    const url = `${ASSET_MANAGEMENT_API.OFFBOARDING_EMPLOYEE_DETAILS}?startDate=${startDate}&endDate=${endDate}`;
    return this.https.get<{ data: OffboardingRequest[] }>(url).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to fetch offboarding data.');
        return throwError(() => error);
      }),
    );
  }

  getStatusDropdownOptions(): Observable<{
    Return: string[];
    Asset: string[];
    Damage: string[];
    Inventory: string[];
  }> {
    const url = ASSET_MANAGEMENT_API.GET_STATUS_DROPDOWN;
    return this.https
      .get<{
        Return: string[];
        Asset: string[];
        Damage: string[];
        Inventory: string[];
      }>(url)
      .pipe(
        catchError((error) => {
          this.toasterService.error('Failed to fetch status dropdown options.');
          return throwError(() => error);
        }),
      );
  }

  // setSelectedCard(cardType: CardType): void {
  //   this.selectedCardSource.next(cardType);
  // }

  getSelectedCard(): CardType | null {
    return this.selectedCardSource.value;
  }

  clearSelectedCard(): void {
    this.selectedCardSource.next(null);
  }

  getVendorStatsData(
    month: string,
    year: string,
  ): Observable<{ data: VendorStatsCard[] }> {
    const url = `${ASSET_MANAGEMENT_API.VENDOR_STATS_CARD}?month=${month}&year=${year}`;
    return this.https.get<{ data: VendorStatsCard[] }>(url).pipe(
      catchError((error) => {
        this.toasterService.error(
          VendorStatsCardEnum.vendorStatsDataFetchFailed,
        );
        return throwError(() => error);
      }),
    );
  }

  getVendorSummaryData(
    startDate: string,
    endDate: string,
  ): Observable<{ data: VendorSummary[] }> {
    const url = `${ASSET_MANAGEMENT_API.VENDOR_CARD_SUMMARY}?startDate=${startDate}&endDate=${endDate}`;
    return this.https.get<{ data: VendorSummary[] }>(url).pipe(
      catchError((error) => {
        this.toasterService.error(
          VendorStatsCardEnum.vendorSummaryDataFetchFailed,
        );
        return throwError(() => error);
      }),
    );
  }

  getOnboardingData(
    startDate: string,
    endDate: string,
  ): Observable<{ data: OnboardingRequest[] }> {
    const url = `${ASSET_MANAGEMENT_API.ONBOARDING_EMPLOYEE_DETAILS}?startDate=${startDate}&endDate=${endDate}`;
    return this.https.get<{ data: OnboardingRequest[] }>(url).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to fetch onboarding data.');
        return throwError(() => error);
      }),
    );
  }

  getDamageAnalyticsData(
    startDate: string,
    endDate: string,
    location: string,
  ): Observable<{ data: DamageRecord[] }> {
    const url = `${ASSET_MANAGEMENT_API.EMPLOYEE_ASSET_ACCESSORIES_INFO}?pickUpStartDate=${startDate}&pickUpEndDate=${endDate}&location=${location}`;
    return this.https.get<{ data: DamageRecord[] }>(url).pipe(
      catchError((error) => {
        this.toasterService.error(
          DamageAnalyticsCardEnum.damageAnalyticsDataFetchFailed,
        );
        return throwError(() => error);
      }),
    );
  }

  createDamageAnalyticsRecord(payload: any): Observable<any> {
    const url = ASSET_MANAGEMENT_API.EMPLOYEE_ASSET_ACCESSORIES;
    return this.https.post(url, payload).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to post damage record  data.');
        return throwError(() => error);
      }),
    );
  }

  updateDamageAnalyticsRecord(record: any): Observable<any> {
    const url = `${ASSET_MANAGEMENT_API.EMPLOYEE_ASSET_ACCESSORIES}`;
    return this.https.put(url, record).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to update damage record data.');
        return throwError(() => error);
      }),
    );
  }

  getEmployeeDetailsByPsid(psid: string): Observable<any> {
    const url = `${ASSET_MANAGEMENT_API.EMPLOYEE_BASIC_INFO}/${psid}`;
    return this.https.get<any>(url).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to fetch employee details by PSID.');
        return throwError(() => error);
      }),
    );
  }

  fetchDamageAnalytics(location: string, month: string): Observable<any[]> {
    const url = `${ASSET_MANAGEMENT_API.DAMAGE_ANALYTICS}?location=${location}&month=${month}`;
    return this.https.get<any[]>(url).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to fetch damage analytics data.');
        return throwError(() => error);
      }),
    );
  }

  searchEmployeesByEmail(email: string): Observable<any> {
    const url = `${ASSET_MANAGEMENT_API.SEARCH_EMPLOYEES_BY_EMAIL}?email=${email}`;
    return this.https.get<any>(url).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to search employees by email.');
        return throwError(() => error);
      }),
    );
  }

  cancelDamageAnalyticsRecord(
    commentItem: string,
    employeeId: string,
    commentsLabel: string,
  ): Observable<any> {
    const url = `${this.baseUrl}/asset-management/cancel-asset`;
    return this.https
      .put(url, {
        employeeId: employeeId,
        comments: commentItem,
        commentsLabel: commentsLabel,
      })
      .pipe(
        map(() => ({ success: true })),
        catchError((error) => {
          this.toasterService.error('Failed to cancel damage record.');
          return throwError(() => error);
        }),
      );
  }
  updateDamageAnalyticsComments(
    commentItem: string,
    employeeId: string,
    commentsLabel: string,
  ): Observable<any> {
    const url = `${this.baseUrl}/asset-management/employee-asset-accessories/comments`;
    return this.https
      .put(url, {
        comments: commentItem,
        employeeId: employeeId,
        commentsLabel: commentsLabel,
      })
      .pipe(
        map(() => ({ success: true })),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  getAssetAllocationData(
    month: string,
    year: string,
  ): Observable<AssetAllocationResponse> {
    const url = `${ASSET_MANAGEMENT_API.ASSETS_CARD_SUMMARY}?month=${month}&year=${year}`;
    return this.https.get<AssetAllocationResponse>(url).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to fetch asset allocation data.');
        return throwError(() => error);
      }),
    );
  }

  processAssetAllocationData(
    apiData: Record<string, Record<string, number>>,
  ): ProcessedAssetData[] {
    const processedData: ProcessedAssetData[] = [];

    // Define MacBook variants (case-insensitive)
    const macBookVariants = ['mac', 'macbook', 'mac book'];

    // Define location mappings for consistency
    const locationMappings: Record<string, string> = {
      gurgaon: 'GGN',
      gurugram: 'GGN',
      noida: 'NOI',
      hyderabad: 'HYD',
    };

    Object.entries(apiData).forEach(([location, devices]) => {
      let macbookCount = 0;
      let windowsCount = 0;

      Object.entries(devices).forEach(([deviceType, count]) => {
        const deviceTypeLower = deviceType.toLowerCase().trim();

        // Check if it's a MacBook variant
        if (
          macBookVariants.some((variant) => deviceTypeLower.includes(variant))
        ) {
          macbookCount += count;
        } else if (deviceType.trim() !== '') {
          // Non-empty, non-Mac devices are considered Windows
          windowsCount += count;
        }
      });

      // Map location to standard abbreviation
      const locationKey = location.toLowerCase();
      const mappedLocation =
        locationMappings[locationKey] || location.toUpperCase().substring(0, 3);

      processedData.push({
        location: mappedLocation.toUpperCase(),
        macbook: macbookCount,
        windows: windowsCount,
      });
    });

    return processedData;
  }

  getOffboardingGetData(
    employeeId: string,
  ): Observable<{ data: OffboardingDetails[] }> {
    const url = `${ASSET_MANAGEMENT_API.GET_RESOURCE_PROFILE_PAGE_INFO}?employeeId=${employeeId}`;
    return this.https.get<{ data: OffboardingDetails[] }>(url).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to fetch data.');
        return throwError(() => error);
      }),
    );
  }

  offboardingComments({
    assetId,
    employeeId,
    commentText,
  }: {
    assetId?: string;
    employeeId: string;
    commentText?: string;
  }): Observable<any> {
    const url = ASSET_MANAGEMENT_API.CREATE_AND_RETRIEVE_COMMENTS;
    if (commentText && assetId) {
      // Add comment (POST)
      const body = { assetId, employeeId, commentText };
      return this.https.post<any>(url, body).pipe(
        catchError((error) => {
          return throwError(() => error);
        }),
      );
    } else {
      // Fetch comments (POST with only employeeId)
      const body = { employeeId };
      return this.https.post<any>(url, body).pipe(
        catchError((error) => {
          return throwError(() => error);
        }),
      );
    }
  }

  //Inventory API Calls
  getAllInventoryItems(): Observable<{ itemId: number; itemName: string }[]> {
    const url = `${ASSET_MANAGEMENT_API.GET_ALL_ITEMS}`;
    return this.https.get<any>(url).pipe(
      map((response) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response.data)) return response.data;
        return [];
      }),
      catchError((err) => {
        this.toasterService.error('Failed to load product list');
        return of([]);
      }),
    );
  }

  getAllNewIssueItems(
    location: string,
  ): Observable<{ itemId: number; itemName: string }[]> {
    const url = `${ASSET_MANAGEMENT_API.GET_ALL_ISSUE_ITEMS}?location=${location}`;
    return this.https.get<any>(url).pipe(
      map((response) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response.data)) return response.data;
        return [];
      }),
      catchError((err) => {
        this.toasterService.error('Failed to load issue product list');
        return of([]);
      }),
    );
  }

  downloadInvoice(url: string) {
    return this.https.get(url, {
      responseType: 'blob',
      headers: { Range: 'bytes=0-' },
    });
  }

  getInventoryData(location: string, month: string | number, year: string | number): Observable<any[]> {
    const url = `${ASSET_MANAGEMENT_API.ITEM_DETAILS}?location=${location}&month=${month}&year=${year}`;
    return this.https.get<any[]>(url).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to fetch inventory data.');
        return throwError(() => error);
      }),
    );
  }

  getInventoryTableData(
    startDate: string,
    endDate: string,
    location: string,
  ): Observable<InventoryTableData[]> {
    const url = `${ASSET_MANAGEMENT_API.ISSUED_ORDER_SUMMARY}?location=${location}&startDate=${startDate}&endDate=${endDate}`;
    return this.https.get<any>(url).pipe(
      map((response: any) => {
        if (Array.isArray(response)) {
          return response;
        } else if (response && Array.isArray(response.data)) {
          return response.data;
        } else if (response && Array.isArray(response.result)) {
          return response.result;
        }

        return []; // fallback
      }),
      catchError((error) => {
        this.toasterService.error('Failed to retrieve inventory items');
        return throwError(() => error);
      }),
    );
  }

  getProcuredSummaryData(
    startDate: string,
    endDate: string,
    location: string,
  ): Observable<InventoryOrderTableData[]> {
    const url = `${ASSET_MANAGEMENT_API.PROCURED_SUMMARY}?location=${location}&startDate=${startDate}&endDate=${endDate}`;
    return this.https.get<any>(url).pipe(
      map((response: any) => {
        if (Array.isArray(response)) {
          return response;
        } else if (response && Array.isArray(response.data)) {
          return response.data;
        } else if (response && Array.isArray(response.result)) {
          return response.result;
        }

        return []; // fallback
      }),
      catchError((error) => {
        this.toasterService.error('Failed to retrieve procured summary data');
        return throwError(() => error);
      }),
    );
  }

  getInventoryItemHistory(
    productId: number,
    subproductId?: number,
    size?: any,
    location?: string,
  ): Observable<any> {
    let url = `${ASSET_MANAGEMENT_API.ITEM_HISTORY}?productId=${productId}`;
    if (subproductId !== undefined && subproductId !== null) {
      url += `&subproductId=${subproductId}`;
    }
    if (size !== undefined && size !== null && size !== 'N/A') {
      url += `&size=${size}`;
    }
    if (location !== undefined && location !== null) {
      url += `&location=${location}`;
    }
    return this.https.get<any>(url).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to retrieve inventory item history');
        return throwError(() => error);
      }),
    );
  }

  addNewInventoryItem(payload: {
    categoryName: string;
    productName: string;
    quantity: number;
    dateOfPurchase: string | null;
    location: string;
    supplierName: string;
    fileAttachment?: string | null;
    createdBy: string;
  }): Observable<any> {
    const url = `${ASSET_MANAGEMENT_API.PROCURED_ITEMS}`;
    return this.https.post<any>(url, payload).pipe(
      map((response: any) => {
        if (Array.isArray(response)) {
          return response;
        } else if (response && Array.isArray(response.data)) {
          return response.data;
        } else if (response && Array.isArray(response.result)) {
          return response.result;
        }

        return []; // fallback
      }),
      catchError((error) => {
        this.toasterService.error('Failed to save add new order item');
        return throwError(() => error);
      }),
    );
  }

  downloadInvoicePreviewImage(procuredOrderId: number) {
    return this.https.get(
      `${ASSET_MANAGEMENT_API.VIEW_INVOICE}/${procuredOrderId}`,
      {
        responseType: 'blob',
      },
    );
  }

  updateInventoryOrderStatus(
    procuredOrderId: number,
    deliveryStatus: string,
  ): Observable<any> {
    const url = `${ASSET_MANAGEMENT_API.UPDATE_DELIVERY_STATUS}/${procuredOrderId}`;
    return this.https
      .put<any>(url, deliveryStatus, {
        headers: { 'Content-Type': 'text/plain' },
        responseType: 'text' as 'json',
      })
      .pipe(
        catchError((error) => {
          this.toasterService.error('Failed to update delivery status');
          return throwError(() => error);
        }),
      );
  }

  updateOrdersAttachmentUrl(
    procuredOrderId: number,
    ordersAttachmentUrl: string,
  ): Observable<any> {
    const url = `${ASSET_MANAGEMENT_API.UPDATE_ORDER_ATTACHMENT}`;
    const params = { procuredOrderId: procuredOrderId };
    const body = {
      ordersAttachmentUrl: ordersAttachmentUrl,
    };

    return this.https.post(url, body, { params }).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to update invoice attachment');
        return throwError(() => error);
      }),
    );
  }

  // bulkIssueUpload(fileName: string): Observable<any> {
  //   const url = `${ASSET_MANAGEMENT_API.BULK_ISSUE_UPLOAD}`;
  //   const body = { issued_items_template_file: fileName };
  //   return this.https.post(url, body).pipe(
  //     catchError((err) => {
  //       this.toasterService.error('Failed to upload bulk issue file');
  //       return throwError(() => err);
  //     }),
  //   );
  // }

  bulkIssueUpload(file: File): Observable<any> {
  const url = `${ASSET_MANAGEMENT_API.BULK_ISSUE_UPLOAD}`;
  const formData = new FormData();
  formData.append('issued_items_template_file', file, file.name);

  return this.https.post(url, formData).pipe(
    catchError((err) => {
      this.toasterService.error('Failed to upload bulk issue file');
      return throwError(() => err);
    }),
  );
}

  bulkProcuredItemsUpload(file: File): Observable<any> {
  const url = `${ASSET_MANAGEMENT_API.BULK_UPLOAD_PROCURED_ITEMS}`;
  const formData = new FormData();
  formData.append('procured_items_template_file', file, file.name);

  return this.https.post(url, formData).pipe(
    catchError((err) => {
      this.toasterService.error('Failed to upload bulk procured items file');
      return throwError(() => err);
    }),
  );
}

  updateIssueOrder(payload: any): Observable<any> {
    const url = `${ASSET_MANAGEMENT_API.UPDATE_ISSUE_ORDER}`;
    return this.https.post(url, payload).pipe(
      catchError((err) => {
        this.toasterService.error('Failed to update issued order');
        return throwError(() => err);
      }),
    );
  }

  editIssueOrder(payload: any): Observable<any> {
    const url = `${ASSET_MANAGEMENT_API.UPDATE_ISSUE_ORDER}`;
    return this.https.put(url, payload).pipe(
      catchError((err) => {
        this.toasterService.error('Failed to edit issued order');
        return throwError(() => err);
      }),
    );
  }

  getAssetRequests(
    startDate: string,
    endDate: string,
  ): Observable<{ data: any[] }> {
    const url = `${ASSET_MANAGEMENT_API.GET_ASSET_REQUESTS}?startDate=${startDate}&endDate=${endDate}`;
    return this.https.get<{ data: any[] }>(url).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to fetch asset requests');
        return throwError(() => error);
      }),
    );
  }

  getEmailNotifications(
    location: string,
  ): Observable<EmailNotificationResponse> {
    const url = `${ASSET_MANAGEMENT_API.LOCATION_EMAIL_NOTIFICATIONS}?location=${location}`;
    return this.https.get<EmailNotificationResponse>(url).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to fetch email notifications');
        return throwError(() => error);
      }),
    );
  }

  getAccessoriesDetails(
    osType: string,
    brandName: string,
    accessoriesTypeId: number,
  ): Observable<any> {
    const url = `${ASSET_MANAGEMENT_API.GET_ACCESSORIES_DETAILS}?osType=${osType}&brandName=${brandName}&accessoriesTypeId=${accessoriesTypeId}`;
    return this.https.get<any>(url).pipe(
      catchError((error) => {
        this.toasterService.error('Failed to fetch accessories details');
        return throwError(() => error);
      }),
    );
  }

  // Renamed to avoid duplicate implementation error
  createAssetRequest(payload: any) {
    const url = ASSET_MANAGEMENT_API.CREATE_ASSET_REQUEST;
    return this.https.post<any>(url, payload).pipe(
      // Show success toast on successful response
      tap(() => {
        this.toasterService.success('Request created successfully');
      }),
      // Show error toast on error
      catchError((error) => {
        this.toasterService.error('Error', error?.error?.message);
        return throwError(() => error);
      }),
    );
  }

  updateAssetRequest(payload: any) {
    const url = ASSET_MANAGEMENT_API.UPDATE_ASSET_REQUEST;
    return this.https.put<any>(url, payload).pipe(
      tap(() => {
        this.toasterService.success('Request updated successfully');
      }),
      catchError((error) => {
        this.toasterService.error('Failed to update asset request');
        return throwError(() => error);
      }),
    );
  }

  getAllAccessoriesAfterUpdate(): Observable<any[]> {
    const url = ASSET_MANAGEMENT_API.ACCESSORIES_API_URL_AFTER_UPDATE;
    return this.https.get<any[]>(url).pipe(
      tap(() => {
        // this.toasterService.success(
        //   'Accessories fetched successfully (after update)',
        // );
      }),
      catchError((error) => {
        // this.toasterService.error('Failed to fetch accessories (after update)');
        return throwError(() => error);
      }),
    );
  }

  createAssetReplace(payload: any) {
    const url = ASSET_MANAGEMENT_API.CREATED_ASSET_REPLACE;
    return this.https.post<any>(url, payload).pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  getProductItems(): Observable<any[]> {
    const url = `${ASSET_MANAGEMENT_API.PRODUCT_ITEMS}`;
    return this.https.get<any>(url).pipe(
      map((response) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response.data)) return response.data;
        return [];
      }),
      catchError((err) => {
        this.toasterService.error('Failed to load product items');
        return of([]);
      }),
    );
  }

  getProductSizes(): Observable<any[]> {
    const url = `${ASSET_MANAGEMENT_API.PRODUCT_SIZES}`;
    return this.https.get<any>(url).pipe(
      map((response) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response.data)) return response.data;
        return [];
      }),
      catchError((err) => {
        this.toasterService.error('Failed to load product sizes');
        return of([]);
      }),
    );
  }

  getGroupedByCategory(): Observable<any[]> {
    const url = `${ASSET_MANAGEMENT_API.GROUPED_BY_CATEGORY}`;
    return this.https.get<any>(url).pipe(
      map((response) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response.data)) return response.data;
        return [];
      }),
      catchError((err) => {
        this.toasterService.error('Failed to load categories');
        return of([]);
      }),
    );
  }
}